import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import axios from 'axios';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import Timeline from '../../inputs/Timeline/Timeline';
import {
  cleanEditedRecords,
  createSchedule,
  createSchedulerSlot,
  defaultScheduleMetadata,
  deleteSchedule,
  deleteSchedulerSlot,
  fetchSchedulerDeviceCatalogue,
  fetchSchedulerProfiles,
  fetchSchedules,
  fetchSchedulerSlots,
  fetchSlotActiveConfigs,

  ISchedule,
  IScheduleRetryPolicy,
  ISchedulerSlot,
  selectDragItemId,
  selectDrawMode,
  selectDrawRecord,
  selectEditedRecords,
  selectRecords,
  selectSchedules,
  selectSchedulerProfiles,
  selectSchedulerSlots,
  selectSchedulerStatus,
  selectSelectedChannel,
  selectSelectedDate,
  selectSelectedRecordId,
  selectSlotActiveConfigs,
  selectToken,
  selectZoom,
  setCreateFromDrawRecord,
  setDragItemId,
  setDrawMode,
  setDrawRecord,
  setSelectedChannel,
  setSelectedDate,
  setSelectedRecordId,
  setViewMode,
  setZoom,
  updateSchedule,
  updateSchedulerSlot,
} from '../../../AppSlice';
import { ConfirmModal } from '../../ConfirmModal';
import { emptyDrawRecord, IDrawEvent, IMonitor, ITimelineItem } from '../../inputs/Timeline/Timeline.model';
import { dateTimeFormat } from '../../../helpers/time';
import { CreateRecordModal } from './CreateRecordModal';
import { mapRecord } from './data';
import RecordItem from './RecordItem';
import RecorderDrawSummary from './RecorderDrawSummary';
import RecorderGalleryView from './RecorderGalleryView';
import RecorderListView from './RecorderListView';
import RecorderLogsView from './RecorderLogsView';
import RecorderToolbar from './RecorderToolbar';
import RecordNowModal, { RecordNowResult } from './RecordNowModal';
import SlotFormModal from './SlotFormModal';
import SlotScheduleModal from './SlotScheduleModal';
import {
  ChannelDetails,
  RecorderFilter,
  RecorderLogFilter,
  RecorderRoute,
  RecordableChannel,
  recorderActions,
  routeActions,
} from './viewModels';
import { GatewayIp } from '../../../data/endpoints';
import { useScheduledRecordingPreviews } from './useScheduledRecordingPreviews';

type SdiResource = {
  id: string;
  label: string;
  config?: Record<string, any> | null;
};

type FilesystemResource = {
  id: string;
  label: string;
  path?: string;
};

type RtmpResource = {
  id: string;
  label: string;
  url?: string;
};

const Recorder: React.FC = () => {
  const dispatch = useAppDispatch();

  const records = useAppSelector(selectRecords);
  const schedules = useAppSelector(selectSchedules);
  const editedRecords = useAppSelector(selectEditedRecords);
  const selectedRecordId = useAppSelector(selectSelectedRecordId);
  const zoom = useAppSelector(selectZoom);
  const dragItemdId = useAppSelector(selectDragItemId);
  const selectedChannel = useAppSelector(selectSelectedChannel);
  const selectedDate = useAppSelector(selectSelectedDate);
  const schedulerStatus = useAppSelector(selectSchedulerStatus);
  const schedulerProfiles = useAppSelector(selectSchedulerProfiles);
  // const schedulerDeviceCatalogue = useAppSelector(selectSchedulerDeviceCatalogue);
  const token = useAppSelector(selectToken);
  const drawMode = useAppSelector(selectDrawMode);
  const drawRecord = useAppSelector(selectDrawRecord);
  const schedulerSlots = useAppSelector(selectSchedulerSlots);
  const slotActiveConfigs = useAppSelector(selectSlotActiveConfigs);

  const monitors: IMonitor[] = useMemo(
    () =>
      schedulerSlots.map((slot) => ({
        id: slot.id,
        title: slot.label,
        isUrlChangeable: false,
        recordServiceType: 'streamUrl' as const,
        isRecordable: slot.enabled,
        ip: '',
      })),
    [schedulerSlots]
  );

  const slotsWithoutActiveConfig = useMemo(() => {
    const missing = new Set<string>();
    schedulerSlots.forEach((slot) => {
      if (slotActiveConfigs[slot.id] === null || slotActiveConfigs[slot.id] === undefined) {
        missing.add(slot.id);
      }
    });
    return missing;
  }, [schedulerSlots, slotActiveConfigs]);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmDeleteRecordId, setConfirmDeleteRecordId] = useState<string>(null);
  const [confirmStopRecordId, setConfirmStopRecordId] = useState<string>(null);
  const [confirmBulkClearOpen, setConfirmBulkClearOpen] = useState(false);
  const [confirmBulkStopOpen, setConfirmBulkStopOpen] = useState(false);
  const [newRecord, setNewRecord] = useState<ITimelineItem>(null);
  const [activeRoute, setActiveRoute] = useState<RecorderRoute>('scheduler');
  const [activeFilter, setActiveFilter] = useState<RecorderFilter>(null);
  const [activeLogFilter, setActiveLogFilter] = useState<RecorderLogFilter>(null);
  const [channelSelections, setChannelSelections] = useState<Record<string, boolean>>({});
  const [items, setItems] = useState<ITimelineItem[]>([]);
  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<ISchedulerSlot | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalInitialStart, setScheduleModalInitialStart] = useState('');
  const [scheduleModalInitialEnd, setScheduleModalInitialEnd] = useState('');
  const [recordNowModalOpen, setRecordNowModalOpen] = useState(false);
  const [recordNowStartAt, setRecordNowStartAt] = useState('');
  const [recordNowEndAt, setRecordNowEndAt] = useState('');
  const [recordNowResults, setRecordNowResults] = useState<RecordNowResult[]>([]);
  const [sdiResources, setSdiResources] = useState<SdiResource[]>([]);
  const [filesystemResources, setFilesystemResources] = useState<FilesystemResource[]>([]);
  const [rtmpResources, setRtmpResources] = useState<RtmpResource[]>([]);
  const scheduledPreviewUrls = useScheduledRecordingPreviews(schedules, token);

  const selectedActionRecordId = dragItemdId || selectedRecordId;
  const selectedActionRecord = items.find((item) => item.id === selectedActionRecordId) ?? null;

  const channelPreviewUrls = useMemo(() => {
    const map: Record<string, string> = {};
    if (!monitors?.length) {
      return map;
    }

    monitors.forEach((monitor) => {
      map[monitor.id] = monitor.ip;
    });

    if (!items?.length) {
      return map;
    }

    const now = moment();
    const nextByChannel: Record<string, { inputUrl: string; candidateMoment: moment.Moment }> = {};

    items.forEach((item) => {
      if (!item?.channel || !item.start) {
        return;
      }

      const scheduleMetadata = (item.options?.metadata ?? {}) as Partial<ISchedule>;
      const inputUrl =
        scheduleMetadata?.params?.input?.url ??
        scheduleMetadata?.params?.inputUrl ??
        (typeof scheduleMetadata?.params?.input === 'string' ? scheduleMetadata?.params?.input : '');
      if (!inputUrl) {
        return;
      }

      const startMoment = moment(item.start);
      if (!startMoment.isValid()) {
        return;
      }

      const endMoment = item.end ? moment(item.end) : null;
      const isActive =
        startMoment.isSameOrBefore(now) &&
        (!endMoment || endMoment.isAfter(now));

      if (!isActive && startMoment.isBefore(now)) {
        return;
      }

      const candidateMoment = isActive ? now : startMoment;
      const existing = nextByChannel[item.channel];

      if (!existing || candidateMoment.isBefore(existing.candidateMoment)) {
        nextByChannel[item.channel] = {
          inputUrl,
          candidateMoment,
        };
      }
    });

    Object.entries(nextByChannel).forEach(([channelId, data]) => {
      map[channelId] = data.inputUrl;
    });

    return map;
  }, [items, monitors]);

  const recordableChannels = useMemo<RecordableChannel[]>(
    () =>
      monitors
        ?.map((channel) => ({
          id: channel.id,
          title: channel.title,
          channelType: channel.recordServiceType,
          monitorPreviewURL: scheduledPreviewUrls[channel.id] ?? channelPreviewUrls[channel.id] ?? channel.ip,
          monitorPreviewType: scheduledPreviewUrls[channel.id] ? 'image' as const : 'stream' as const,
          enabled: channel.isRecordable,
        })) ?? [],
    [channelPreviewUrls, monitors, scheduledPreviewUrls]
  );

  useEffect(() => {
    setChannelSelections((current) => {
      const nextSelections = { ...current };
      let hasChanges = false;

      monitors.forEach((monitor) => {
        if (typeof nextSelections[monitor.id] !== 'boolean') {
          nextSelections[monitor.id] = false;
          hasChanges = true;
          return;
        }

        if (!monitor.isRecordable && nextSelections[monitor.id] !== false) {
          nextSelections[monitor.id] = false;
          hasChanges = true;
        }
      });

      return hasChanges ? nextSelections : current;
    });
  }, [monitors]);

  const conflictRecordIds = useMemo(() => {
    const conflictIds = new Set<string>();
    const byChannel = new Map<string, ITimelineItem[]>();

    items.forEach((item) => {
      if (!item.channel || !item.start || !item.end) {
        return;
      }

      const channelItems = byChannel.get(item.channel) ?? [];
      channelItems.push(item);
      byChannel.set(item.channel, channelItems);
    });

    byChannel.forEach((channelItems) => {
      const sorted = [...channelItems].sort(
        (a, b) => moment(a.start).valueOf() - moment(b.start).valueOf()
      );

      for (let index = 1; index < sorted.length; index += 1) {
        const previous = sorted[index - 1];
        const current = sorted[index];

        if (moment(previous.end).isAfter(moment(current.start))) {
          conflictIds.add(previous.id);
          conflictIds.add(current.id);
        }
      }
    });

    return conflictIds;
  }, [items]);

  const filteredItems = useMemo(() => {
    const now = moment();

    return items.filter((item) => {
      if (!activeFilter) {
        return true;
      }

      const itemStart = item.start ? moment(item.start) : null;
      const itemEnd = item.end ? moment(item.end) : null;
      const isPaused = item.status === "0";
      const isRecording =
        !isPaused &&
        Boolean(itemStart?.isValid()) &&
        itemStart.isSameOrBefore(now) &&
        (!itemEnd || itemEnd.isAfter(now));
      const isStandby =
        !isPaused &&
        Boolean(itemStart?.isValid()) &&
        itemStart.isAfter(now);
      const isConflict = conflictRecordIds.has(item.id);

      switch (activeFilter) {
        case 'recording':
          return isRecording;
        case 'paused':
          return isPaused;
        case 'standby':
          return isStandby;
        case 'conflict':
          return isConflict;
        default:
          return true;
      }
    });
  }, [activeFilter, conflictRecordIds, items]);

  const filterCounts = useMemo(() => {
    const now = moment();

    return items.reduce(
      (acc, item) => {
        const itemStart = item.start ? moment(item.start) : null;
        const itemEnd = item.end ? moment(item.end) : null;
        const isPaused = item.status === "0";
        const isRecording =
          !isPaused &&
          Boolean(itemStart?.isValid()) &&
          itemStart.isSameOrBefore(now) &&
          (!itemEnd || itemEnd.isAfter(now));
        const isStandby =
          !isPaused &&
          Boolean(itemStart?.isValid()) &&
          itemStart.isAfter(now);

        if (isRecording) acc.recording += 1;
        if (isPaused) acc.paused += 1;
        if (isStandby) acc.standby += 1;
        if (conflictRecordIds.has(item.id)) acc.conflict += 1;

        return acc;
      },
      { recording: 0, paused: 0, standby: 0, conflict: 0 }
    );
  }, [conflictRecordIds, items]);

  const logFilterCounts = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        const isConflict = conflictRecordIds.has(item.id);
        const isPaused = item.status === "0";
        if (isConflict) acc.conflict += 1;
        else if (isPaused) acc.paused += 1;
        else acc.scheduled += 1;
        return acc;
      },
      { scheduled: 0, paused: 0, conflict: 0 }
    );
  }, [conflictRecordIds, items]);

  const logFilteredItems = useMemo(() => {
    if (!activeLogFilter) return items;
    return items.filter((item) => {
      const isConflict = conflictRecordIds.has(item.id);
      const isPaused = item.status === "0";
      switch (activeLogFilter) {
        case 'conflict': return isConflict;
        case 'paused': return isPaused;
        case 'scheduled': return !isConflict && !isPaused;
        default: return true;
      }
    });
  }, [activeLogFilter, conflictRecordIds, items]);

  const channelDetailsById = useMemo<Record<string, ChannelDetails>>(() => {
    const detailsById: Record<string, ChannelDetails> = {};

    recordableChannels.forEach((channel, index) => {
      const now = moment();
      const channelItems = filteredItems
        .filter((item) => item.channel === channel.id)
        .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
      const activeItem = channelItems.find((item) => {
        const itemStart = item.start ? moment(item.start) : null;
        const itemEnd = item.end ? moment(item.end) : null;

        return Boolean(
          itemStart?.isValid() &&
          itemStart.isSameOrBefore(now) &&
          (!itemEnd || itemEnd.isAfter(now))
        );
      });
      const latestItem = activeItem ?? channelItems[channelItems.length - 1] ?? null;
      const metadata = (latestItem?.options?.metadata ?? {}) as Partial<ISchedule>;
      const signal =
        metadata?.params?.input?.video?.sourceId?.toString() ??
        metadata?.params?.monitorId?.toString() ??
        `SDI-${index + 1}`;
      const status = latestItem
        ? conflictRecordIds.has(latestItem.id)
          ? 'Conflict'
          : latestItem.status === "0"
            ? 'Paused'
            : activeItem
              ? 'Recording'
              : 'Stand-by'
        : 'Stand-by';

      detailsById[channel.id] = {
        activeItem,
        latestItem,
        signal,
        status,
      };
    });

    return detailsById;
  }, [conflictRecordIds, filteredItems, recordableChannels]);

  const selectedChannelIdsForRecordForm = useMemo(
    () =>
      recordableChannels
        .filter((channel) => channel.enabled)
        .filter((channel) => channelSelections[channel.id] ?? true)
        .map((channel) => channel.id),
    [channelSelections, recordableChannels]
  );

  const selectedChannelIdsForImmediateRecord = useMemo(
    () =>
      selectedChannelIdsForRecordForm.filter((channelId) => !slotsWithoutActiveConfig.has(channelId)),
    [selectedChannelIdsForRecordForm, slotsWithoutActiveConfig]
  );

  const selectedSlotCount = selectedChannelIdsForRecordForm.length;

  const futureRecordIdsForSelectedSlots = useMemo(() => {
    const now = moment();
    const selectedChannelSet = new Set(selectedChannelIdsForRecordForm);

    return items
      .filter((item) => item.id !== 'new_0')
      .filter((item) => selectedChannelSet.has(item.channel))
      .filter((item) => item.start && moment(item.start).isAfter(now))
      .map((item) => item.id);
  }, [items, selectedChannelIdsForRecordForm]);

  const activeRecordIdsForSelectedSlots = useMemo(() => {
    const now = moment();
    const selectedChannelSet = new Set(selectedChannelIdsForRecordForm);

    return items
      .filter((item) => item.id !== 'new_0')
      .filter((item) => selectedChannelSet.has(item.channel))
      .filter((item) => {
        const itemStart = item.start ? moment(item.start) : null;
        const itemEnd = item.end ? moment(item.end) : null;

        return Boolean(
          itemStart?.isValid() &&
          itemStart.isSameOrBefore(now) &&
          (!itemEnd || itemEnd.isAfter(now))
        );
      })
      .map((item) => item.id);
  }, [items, selectedChannelIdsForRecordForm]);

  const recorderActionConfigs = useMemo(
    () => ({
      ...recorderActions,
      record: {
        ...recorderActions.record,
        title: `Record Now ( ${selectedChannelIdsForImmediateRecord.length} )`,
      },
      stop: {
        ...recorderActions.stop,
        title: `Stop ( ${activeRecordIdsForSelectedSlots.length} )`,
      },
      clear: {
        ...recorderActions.clear,
        title: `Clear ( ${selectedSlotCount} )`,
      },
    }),
    [activeRecordIdsForSelectedSlots.length, selectedChannelIdsForImmediateRecord.length, selectedSlotCount]
  );

  const actionStates = useMemo(
    () => ({
      record: selectedChannelIdsForImmediateRecord.length === 0,
      schedule: selectedChannelIdsForRecordForm.length === 0,
      pause: !selectedActionRecord || selectedActionRecord.id === 'new_0' || selectedActionRecord.status === "0",
      stop: activeRecordIdsForSelectedSlots.length === 0,
      clear: futureRecordIdsForSelectedSlots.length === 0,
    }),
    [activeRecordIdsForSelectedSlots.length, futureRecordIdsForSelectedSlots.length, selectedActionRecord, selectedChannelIdsForImmediateRecord.length, selectedChannelIdsForRecordForm.length]
  );

  const onClickFreespace = () => {
    if (!window['clickInItem']) {
      dispatch(setSelectedRecordId(null));
      dispatch(setDragItemId(null));
    }
    window['clickInItem'] = false;
  };

  useEffect(() => {
    if (selectedRecordId && selectedRecordId !== "new_0") {
      setNewRecord(items.find((item) => item.id === selectedRecordId));
      setEditModalOpen(true);
    }
  }, [items, selectedRecordId]);

  useEffect(() => {
    if (!selectedDate) {
      if (records.length > 0) {
        const earliestRecord = [...records]
          .filter((record) => record?.startedAt)
          .sort((a, b) => moment(a.startedAt).valueOf() - moment(b.startedAt).valueOf())[0];
        if (earliestRecord?.startedAt) {
          dispatch(setSelectedDate(moment(earliestRecord.startedAt).startOf('day').toISOString()));
          return;
        }
      }

      dispatch(setSelectedDate(moment().startOf('day').toISOString()));
    }
  }, [dispatch, records, selectedDate]);

  useEffect(() => {
    if (token) {
      dispatch(fetchSchedules({ token }));
      dispatch(fetchSchedulerSlots({ token }));
      dispatch(fetchSchedulerProfiles({ token }));
      dispatch(fetchSchedulerDeviceCatalogue({ token }));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${GatewayIp}/api/resources`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const data = response.data;
        const all: Array<{ id: string; label: string; resourceType: string; direction?: string; enabled?: boolean; capabilities?: string[] | null; config?: Record<string, any> | null }> = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.content)
              ? data.content
              : [];
        setSdiResources(
          all
            .filter((r) => {
              if (r.resourceType !== 'SDI' || r.enabled === false) return false;
              const hasDeviceIndex = r.config?.deviceIndex != null && r.config?.deviceIndex !== '';
              const hasSdiUrl = typeof r.config?.url === 'string' && r.config.url.toLowerCase().startsWith('sdi://');
              return hasDeviceIndex || hasSdiUrl;
            })
            .map(({ id, label, config }) => ({ id, label, config }))
        );
        setFilesystemResources(
          all
            .filter((r) => r.enabled !== false && Array.isArray(r.capabilities) && r.capabilities.includes('FILE_BASED_WRITE'))
            .map(({ id, label, config }) => ({ id, label, path: (config?.path ?? config?.url ?? '').toString() || undefined }))
        );
        setRtmpResources(
          all
            .filter((r) => {
              const resourceType = (r.resourceType ?? '').toUpperCase();
              const direction = (r.direction ?? '').toUpperCase();
              return resourceType === 'RTMP'
                && r.enabled !== false
                && (direction === 'TARGET' || direction === 'BOTH');
            })
            .map(({ id, label, config }) => ({
              id,
              label,
              url: (config?.url ?? config?.serverUrl ?? '').toString() || undefined,
            }))
        );
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (token && schedulerSlots.length > 0) {
      dispatch(fetchSlotActiveConfigs({ token, slotIds: schedulerSlots.map((s) => s.id) }));
    }
  }, [dispatch, token, schedulerSlots]);

  const anyModalOpenRef = useRef(false);
  anyModalOpenRef.current = editModalOpen || scheduleModalOpen || slotModalOpen || recordNowModalOpen;

  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      if (anyModalOpenRef.current) return;
      dispatch(fetchSchedules({ token }));
      dispatch(fetchSchedulerSlots({ token }));
    }, 10000);
    return () => clearInterval(id);
  }, [dispatch, token]);

  useEffect(() => {
    if (!editModalOpen) {
      dispatch(setSelectedRecordId(null));
      dispatch(cleanEditedRecords());
    }
  }, [dispatch, editModalOpen]);

  useEffect(() => {
    const editItems: ITimelineItem[] = [];

    records.forEach((item) => {
      if (item?.monitor) {
        editItems.push(mapRecord(item));
      }
    });

    Object.values(editedRecords).forEach((item) => {
      if (item?.monitor) {
        editItems.push(mapRecord(item));
      }
    });

    setItems(editItems);
  }, [drawRecord, editedRecords, records, selectedChannel]);

  const onDelete = (id: string) => {
    if (!id) {
      setConfirmDeleteRecordId(null);
      return;
    }

    if (id === "new_0") {
      dispatch(cleanEditedRecords());
      dispatch(setSelectedRecordId(null));
      setNewRecord(null);
      setConfirmDeleteRecordId(null);
      return;
    }

    if (!token) {
      console.warn("Unable to delete schedule without authentication token");
      setConfirmDeleteRecordId(null);
      return;
    }

    dispatch(deleteSchedule({ token, scheduleId: id }));
    dispatch(setSelectedRecordId(null));
    setConfirmDeleteRecordId(null);
  };

  const onStop = (id: string) => {
    if (!id) {
      setConfirmStopRecordId(null);
      return;
    }

    if (!token) {
      console.warn("Unable to stop schedule without authentication token");
      setConfirmStopRecordId(null);
      return;
    }

    dispatch(updateSchedule({
      token,
      scheduleId: id,
      schedulePatch: {
        endAt: moment().add(10, 'seconds').utc().toISOString(),
      },
    }));
    dispatch(setSelectedRecordId(null));
    setConfirmStopRecordId(null);
  };

  const onRecordCreated = (item: IDrawEvent) => {
    const monitor = monitors.find((monitorItem) => monitorItem.id === item.channel);
    const targetChannelIds =
      item.channelIds && item.channelIds.length > 0
        ? item.channelIds
        : selectedChannelIdsForRecordForm.length > 0
          ? selectedChannelIdsForRecordForm
          : item.channel
            ? [item.channel]
            : [];
    const scheduleMetadata = {
      ...defaultScheduleMetadata,
      label: `${monitor?.title ?? "Record"} - ${dateTimeFormat(item.startTime)}`,
      startAt: item.startTime,
      endAt: item.endTime,
      params: {
        ...defaultScheduleMetadata.params,
        monitorId: item.channel,
        monitorIds: targetChannelIds,
        input: {
          ...(defaultScheduleMetadata.params?.input ?? {}),
          url: monitor?.ip ?? defaultScheduleMetadata.params?.input?.url ?? "",
          video: {
            ...((defaultScheduleMetadata.params?.input?.video ?? {}) as Record<string, any>),
            sourceId: item.channel,
          },
        },
        output: {
          ...(defaultScheduleMetadata.params?.output ?? {}),
          url: defaultScheduleMetadata.params?.output?.url ?? "c://records/record.mp4",
        },
        additionalArgs: defaultScheduleMetadata.params?.additionalArgs ?? [],
      },
      retryPolicy: {
        ...defaultScheduleMetadata.retryPolicy,
      },
    };

    setNewRecord({
      id: "new_0",
      title: scheduleMetadata.label,
      start: item.startTime,
      end: item.endTime,
      length: moment.duration(moment(item.endTime).diff(moment(item.startTime))).asSeconds(),
      playlistLength: moment.duration(moment(item.endTime).diff(moment(item.startTime))).asSeconds(),
      channel: item.channel,
      status: "0",
      options: {
        itemType: 'record',
        metadata: scheduleMetadata,
      },
    });
    setEditModalOpen(true);
    dispatch(setCreateFromDrawRecord());
  };

  const onStartAddRecordDraw = () => {
    setActiveRoute('scheduler');
    dispatch(setViewMode("calendar"));
    dispatch(setDrawRecord({
      ...emptyDrawRecord,
      channelIds: selectedChannelIdsForRecordForm,
    }));
    dispatch(setSelectedRecordId(null));
    dispatch(setDragItemId(null));
    dispatch(setDrawMode(true));
  };

  const onEditRecord = () => {
    if (!selectedActionRecord) {
      return;
    }

    setNewRecord(selectedActionRecord);
    dispatch(setSelectedRecordId(selectedActionRecord.id));
    setEditModalOpen(true);
  };

  const onPauseRecord = () => {
    if (!token || !selectedActionRecord || selectedActionRecord.id === 'new_0') {
      return;
    }

    const metadata = (selectedActionRecord.options?.metadata ?? {}) as Partial<ISchedule>;
    dispatch(updateSchedule({
      token,
      scheduleId: selectedActionRecord.id,
      schedulePatch: {
        ...metadata,
        enabled: false,
      },
    }));
  };

  const clearFutureRecordsOnSelectedSlots = async () => {
    if (!token || futureRecordIdsForSelectedSlots.length === 0) {
      return;
    }

    await Promise.all(
      futureRecordIdsForSelectedSlots.map((scheduleId) =>
        dispatch(deleteSchedule({ token, scheduleId }))
      )
    );

    dispatch(setSelectedRecordId(null));
    dispatch(setDragItemId(null));
    setConfirmBulkClearOpen(false);
    dispatch(setDrawRecord(emptyDrawRecord));
    dispatch(setDrawMode(false));
    refreshSchedules();
  };

  const stopActiveRecordsOnSelectedSlots = async () => {
    if (!token || activeRecordIdsForSelectedSlots.length === 0) {
      return;
    }

    const stopAt = moment().add(10, 'seconds').utc().toISOString();

    await Promise.all(
      activeRecordIdsForSelectedSlots.map((scheduleId) =>
        dispatch(updateSchedule({
          token,
          scheduleId,
          schedulePatch: {
            endAt: stopAt,
          },
        }))
      )
    );

    dispatch(setSelectedRecordId(null));
    dispatch(setDragItemId(null));
    setConfirmBulkStopOpen(false);
    refreshSchedules();
  };

  const refreshSchedules = () => {
    if (token) {
      dispatch(fetchSchedules({ token }));
      dispatch(fetchSchedulerSlots({ token }));
    }
  };

  const buildImmediateRecordWindow = () => {
    const start = moment().add(15, 'seconds');
    const end = moment(start).add(3, 'hours');

    return {
      startAtIso: start.utc().toISOString(),
      endAtIso: end.utc().toISOString(),
      startAtInput: start.format('YYYY-MM-DDTHH:mm'),
      endAtInput: end.format('YYYY-MM-DDTHH:mm'),
    };
  };

  const createRecordNowForSlots = async (slotIds: string[]) => {
    if (!token || slotIds.length === 0) {
      return;
    }

    const { startAtIso, endAtIso } = buildImmediateRecordWindow();
    const nextResults = await Promise.all(
      slotIds.map(async (slotId) => {
        const slotLabel = schedulerSlots.find((slot) => slot.id === slotId)?.label ?? slotId;
        const activeConfig = slotActiveConfigs[slotId];
        try {
          const schedule: Partial<ISchedule> = {
            slotId,
            label: `${slotLabel} - ${moment(startAtIso).format('DD.MM.YYYY HH:mm')}`,
            taskType: activeConfig?.taskType,
            params: activeConfig?.params ?? {},
            startAt: startAtIso,
            endAt: endAtIso,
            tz: activeConfig?.tz ?? 'Europe/Vienna',
            overlapPolicy: activeConfig?.overlapPolicy ?? 'SKIP_IF_RUNNING',
            misfirePolicy: activeConfig?.misfirePolicy ?? 'START_IF_BEFORE_END',
            retryPolicy: activeConfig?.retryPolicy ?? {
              backoff: 'exponential',
              initialMs: 1000,
              maxAttempts: 3,
            },
            enabled: true,
          };
          const result = await dispatch(createSchedule({ token, schedule })).unwrap();
          return {
            slotId,
            slotLabel,
            scheduleId: result.schedule?.id,
            status: 'success' as const,
          };
        } catch (error: any) {
          return {
            slotId,
            slotLabel,
            status: 'error' as const,
            message: error?.message ?? error?.response?.data?.message ?? 'Failed',
          };
        }
      })
    );

    setRecordNowStartAt(startAtIso);
    setRecordNowEndAt(endAtIso);
    setRecordNowResults(nextResults);
    setRecordNowModalOpen(true);
    refreshSchedules();
  };

  const handleRecordNow = () => {
    void createRecordNowForSlots(selectedChannelIdsForImmediateRecord);
  };

  const handleRecordChannelNow = (channelId: string) => {
    void createRecordNowForSlots([channelId]);
  };

  const handleScheduleFromDefault = () => {
    setScheduleModalInitialStart('');
    setScheduleModalInitialEnd('');
    setScheduleModalOpen(true);
  };

  const handleAddSlot = () => {
    setEditingSlot(null);
    setSlotModalOpen(true);
  };

  const handleEditSlot = (channelId: string) => {
    const slot = schedulerSlots.find((s) => s.id === channelId) ?? null;
    setEditingSlot(slot);
    setSlotModalOpen(true);
  };

  const handleSaveSlot = (data: { label: string; enabled: boolean; description: string }) => {
    if (!token) return;
    if (editingSlot) {
      dispatch(updateSchedulerSlot({ token, slotId: editingSlot.id, patch: data }));
    } else {
      dispatch(createSchedulerSlot({ token, slot: data }));
    }
    setSlotModalOpen(false);
    setEditingSlot(null);
    refreshSchedules();
  };

  const handleDeleteSlot = (slotId: string) => {
    if (!token) return;
    dispatch(deleteSchedulerSlot({ token, slotId }));
    setSlotModalOpen(false);
    setEditingSlot(null);
  };

  const onActionClick = (action: string) => {
    switch (action) {
      case 'add':
        onStartAddRecordDraw();
        break;
      case 'edit':
        onEditRecord();
        break;
      case 'record':
        void handleRecordNow();
        break;
      case 'schedule':
        handleScheduleFromDefault();
        break;
      case 'pause':
        onPauseRecord();
        break;
      case 'stop':
        setConfirmBulkStopOpen(true);
        break;
      case 'clear':
        setConfirmBulkClearOpen(true);
        break;
      case 'refresh':
        refreshSchedules();
        break;
      default:
        break;
    }
  };

  const getStatusClass = (status: RecorderFilter): string => {
    switch (status) {
      case 'recording':
        return 'progress-success';
      case 'paused':
        return 'progress-warning';
      case 'standby':
        return 'progress-default';
      case 'conflict':
        return 'progress-danger';
      default:
        return 'progress-default';
    }
  };

  const handleToggleChannelSelection = (channelId: string, checked: boolean) => {
    const channel = recordableChannels.find((item) => item.id === channelId);
    if (channel?.enabled === false) {
      return;
    }

    setChannelSelections((current) => ({
      ...current,
      [channelId]: checked,
    }));
  };

  const handleToggleAllChannelSelections = (checked: boolean) => {
    setChannelSelections((current) => {
      const nextSelections = { ...current };

      recordableChannels.forEach((channel) => {
        nextSelections[channel.id] = channel.enabled ? checked : false;
      });

      return nextSelections;
    });
  };

  return (
    <>
      <div className="recorder-div">
        <RecorderToolbar
          activeFilter={activeFilter}
          activeLogFilter={activeLogFilter}
          activeRoute={activeRoute}
          actionConfigs={recorderActionConfigs}
          actionStates={actionStates}
          filterCounts={filterCounts}
          logFilterCounts={logFilterCounts}
          getStatusClass={getStatusClass}
          onActionClick={onActionClick}
          onAddSlot={handleAddSlot}
          clearCount={selectedSlotCount}
          onFilterChange={setActiveFilter}
          onLogFilterChange={setActiveLogFilter}
          onRouteChange={setActiveRoute}
          recordNowCount={selectedChannelIdsForImmediateRecord.length}
          stopCount={activeRecordIdsForSelectedSlots.length}
          routeConfigs={routeActions}
        />

        {activeRoute === 'scheduler' ? (
          <Timeline
            panelName="recorder"
            editMode={true}
            timelineItems={filteredItems}
            droppableType={"recorder"}
            zoom={zoom}
            selectedChannel={selectedChannel}
            channels={recordableChannels}
            startDate={selectedDate}
            endDate={null}
            calendarSize={"lg"}
            onSelectChannel={(channelId) => dispatch(setSelectedChannel(channelId))}
            onZoomChange={(val) => dispatch(setZoom(val))}
            onStartDateChange={(date) => dispatch(setSelectedDate(date))}
            onSubmit={() => console.log("submit data...")}
            timelineStatus={schedulerStatus}
            submittedNow={schedulerStatus === 'loading'}
            hoursBeforeDay={24}
            hoursAfterDay={24}
            onClickFreespace={onClickFreespace}
            TimelineItemComponent={RecordItem}
            drawMode={drawMode}
            drawEvent={drawRecord}
            setDrawEvent={(event) => dispatch(setDrawRecord(event))}
            setDrawMode={(event) => dispatch(setDrawMode(event))}
            onDragItem={() => {}}
            onCreateDrawEvent={onRecordCreated}
            dragItemdId={dragItemdId}
            setDragItemId={(id) => dispatch(setDragItemId(id))}
            channelSelections={channelSelections}
            selectedDrawChannelIds={selectedChannelIdsForRecordForm}
            onToggleChannelSelection={handleToggleChannelSelection}
            onEditChannel={handleEditSlot}
            onRecordChannel={handleRecordChannelNow}
            recordDisabledChannelIds={recordableChannels.filter((channel) => !channel.enabled || slotsWithoutActiveConfig.has(channel.id)).map((channel) => channel.id)}
            selectionDisabledChannelIds={recordableChannels.filter((channel) => !channel.enabled).map((channel) => channel.id)}
          />
        ) : null}

        {activeRoute === 'list' ? (
          <RecorderListView
            channelDetailsById={channelDetailsById}
            channelSelections={channelSelections}
            onEditSlot={handleEditSlot}
            onRecordChannel={handleRecordChannelNow}
            onToggleAllChannelSelections={handleToggleAllChannelSelections}
            onToggleChannelSelection={handleToggleChannelSelection}
            recordableChannels={recordableChannels}
            slotsWithoutActiveConfig={slotsWithoutActiveConfig}
          />
        ) : null}

        {activeRoute === 'gallery' ? (
          <RecorderGalleryView
            channelDetailsById={channelDetailsById}
            channelSelections={channelSelections}
            onEditSlot={handleEditSlot}
            onRecordChannel={handleRecordChannelNow}
            onStopChannel={(recordId) => setConfirmStopRecordId(recordId)}
            onToggleChannelSelection={handleToggleChannelSelection}
            recordableChannels={recordableChannels}
            slotsWithoutActiveConfig={slotsWithoutActiveConfig}
          />
        ) : null}

        {activeRoute === 'logs' ? (
          <RecorderLogsView
            conflictRecordIds={conflictRecordIds}
            filteredItems={logFilteredItems}
          />
        ) : null}

        <RecorderDrawSummary drawMode={drawMode} drawRecord={drawRecord} />
      </div>

      <CreateRecordModal
        initialSelectedChannelIds={selectedChannelIdsForRecordForm}
        existingItems={items}
        monitors={monitors}
        open={editModalOpen}
        record={newRecord}
        mediaengineProfiles={schedulerProfiles}
        sdiResources={sdiResources}
        filesystemResources={filesystemResources}
        rtmpResources={rtmpResources}
        slotActiveConfigs={slotActiveConfigs}
        token={token}
        onClose={() => setEditModalOpen(false)}
        onDelete={(id) => {
          if (id === "new_0") {
            setEditModalOpen(false);
            setNewRecord(null);
            dispatch(setSelectedRecordId(null));
            return;
          }

          setEditModalOpen(false);
          const recordToChange = items.find((item) => item.id === id) ?? newRecord;
          const now = moment();
          const start = recordToChange?.start ? moment(recordToChange.start) : null;
          const end = recordToChange?.end ? moment(recordToChange.end) : null;
          const isActiveRecord = Boolean(
            start?.isValid() &&
            start.isSameOrBefore(now) &&
            end?.isValid() &&
            end.isAfter(now)
          );

          if (isActiveRecord) {
            setConfirmStopRecordId(id);
            return;
          }

          setConfirmDeleteRecordId(id);
        }}
        onSubmit={(data, schedulePayload) => {
          if (!token) {
            console.warn("Unable to submit schedule without authentication token");
            return;
          }

          const selectedChannelIds = Array.isArray(schedulePayload?.params?.monitorIds) && schedulePayload.params.monitorIds.length > 0
            ? schedulePayload.params.monitorIds
                .map((channelId) => (channelId ?? '').toString().trim())
                .filter((channelId, index, array) => channelId.length > 0 && array.indexOf(channelId) === index)
            : [(data.channel ?? '').toString().trim()].filter(Boolean);

          const mergedRetryPolicy: IScheduleRetryPolicy = {
            ...(defaultScheduleMetadata.retryPolicy ?? {}),
            ...(schedulePayload?.retryPolicy ?? {}),
          };

          const buildScheduleRequestForChannel = (channelId: string) => {
            const monitor = monitors.find((monitorItem) => monitorItem.id === channelId);
            const channelConfig = (schedulePayload?.params?.channelConfigs as Record<string, any> | undefined)?.[channelId];
            const channelTaskType = (channelConfig?.taskType ?? schedulePayload?.taskType ?? defaultScheduleMetadata.taskType ?? "FFMPEG_RECORD").toString();
            const paramsForChannel: Record<string, any> =
              channelTaskType === "MEDIAENGINE_RECORD"
                ? {
                    ...(schedulePayload?.params ?? {}),
                    monitorId: channelId,
                    monitorIds: [channelId],
                    channelConfigs: channelConfig ? { [channelId]: channelConfig } : {},
                    input: {
                      ...((channelConfig?.input ?? schedulePayload?.params?.input ?? {}) as Record<string, any>),
                      url:
                        channelConfig?.input?.url ??
                        (
                          monitor?.ip && schedulePayload?.params?.input?.type !== 'sdi'
                            ? monitor.ip
                            : ((schedulePayload?.params?.input?.url ?? '') as string)
                        ),
                      video: {
                        ...((channelConfig?.input?.video ?? schedulePayload?.params?.input?.video ?? {}) as Record<string, any>),
                        sourceId: (
                          channelConfig?.input?.video?.sourceId ??
                          (
                            schedulePayload?.params?.input?.type === 'sdi'
                              ? schedulePayload?.params?.input?.video?.sourceId
                              : channelId
                          )
                        )?.toString() ?? '',
                      },
                    },
                    output: {
                      ...((channelConfig?.output ?? schedulePayload?.params?.output ?? {}) as Record<string, any>),
                    },
                    filename: (channelConfig?.filename ?? schedulePayload?.params?.filename ?? '').toString(),
                    profile: (channelConfig?.profile ?? schedulePayload?.params?.profile ?? '').toString(),
                    realtime:
                      typeof channelConfig?.realtime === 'boolean'
                        ? channelConfig.realtime
                        : schedulePayload?.params?.realtime,
                    additionalArgs:
                      Array.isArray(channelConfig?.additionalArgs)
                        ? channelConfig.additionalArgs
                        : Array.isArray(schedulePayload?.params?.additionalArgs)
                          ? schedulePayload?.params?.additionalArgs
                          : [],
                  }
                : channelTaskType === "DUMMY_HEARTBEAT"
                  ? {
                      monitorId: channelId,
                      monitorIds: [channelId],
                      channelConfigs: channelConfig ? { [channelId]: channelConfig } : {},
                      file: (channelConfig?.heartbeatFile ?? schedulePayload?.params?.file ?? '').toString(),
                      intervalMs: Number(channelConfig?.heartbeatIntervalMs ?? schedulePayload?.params?.intervalMs ?? 1000),
                      message: (channelConfig?.heartbeatMessage ?? schedulePayload?.params?.message ?? '').toString(),
                    }
                : {
                    ...(defaultScheduleMetadata.params ?? {}),
                    ...(schedulePayload?.params ?? {}),
                    monitorId: channelId,
                    monitorIds: [channelId],
                    channelConfigs: channelConfig ? { [channelId]: channelConfig } : {},
                    input: channelConfig?.input?.url ?? schedulePayload?.params?.input ?? schedulePayload?.params?.inputUrl,
                    inputUrl: channelConfig?.input?.url ?? schedulePayload?.params?.inputUrl ?? schedulePayload?.params?.input,
                    output: channelConfig?.output?.url ?? schedulePayload?.params?.output ?? '',
                    profile: (channelConfig?.profile ?? schedulePayload?.params?.profile ?? '').toString(),
                    realtime:
                      typeof channelConfig?.realtime === 'boolean'
                        ? channelConfig.realtime
                        : schedulePayload?.params?.realtime,
                    additionalArgs:
                      Array.isArray(channelConfig?.additionalArgs)
                        ? channelConfig.additionalArgs
                        : Array.isArray(schedulePayload?.params?.additionalArgs)
                          ? schedulePayload?.params?.additionalArgs
                          : [],
                  };

            const scheduleRequest: Partial<ISchedule> & {
              params: Record<string, any>;
              retryPolicy: IScheduleRetryPolicy;
            } = {
              slotId: channelId,
              label: schedulePayload?.label ?? data.title,
              taskType: channelTaskType,
              startAt: moment(data.start).utc().toISOString(),
              endAt: moment(data.end).utc().toISOString(),
              tz: schedulePayload?.tz ?? defaultScheduleMetadata.tz,
              overlapPolicy: schedulePayload?.overlapPolicy ?? defaultScheduleMetadata.overlapPolicy,
              misfirePolicy: schedulePayload?.misfirePolicy ?? defaultScheduleMetadata.misfirePolicy,
              params: paramsForChannel,
              retryPolicy: mergedRetryPolicy,
              enabled: schedulePayload?.enabled ?? defaultScheduleMetadata.enabled ?? true,
            };

            if (channelTaskType === "FFMPEG_RECORD" && !scheduleRequest.params?.inputUrl) {
              scheduleRequest.params.inputUrl = monitor?.ip ?? "";
            }

            return scheduleRequest;
          };

          if (data.id === "new_0") {
            selectedChannelIds.forEach((channelId) => {
              dispatch(createSchedule({ token, schedule: buildScheduleRequestForChannel(channelId) }));
            });
          } else {
            const currentChannelId = (newRecord?.channel ?? data.channel ?? '').toString().trim();
            const keepCurrentChannel = currentChannelId.length > 0 && selectedChannelIds.includes(currentChannelId);

            if (keepCurrentChannel) {
              dispatch(updateSchedule({
                token,
                scheduleId: data.id,
                schedulePatch: buildScheduleRequestForChannel(currentChannelId),
              }));
            }

            selectedChannelIds
              .filter((channelId) => channelId !== currentChannelId)
              .forEach((channelId) => {
              dispatch(createSchedule({ token, schedule: buildScheduleRequestForChannel(channelId) }));
              });

            if (!keepCurrentChannel) {
              dispatch(deleteSchedule({ token, scheduleId: data.id }));
            }
          }
        }}
      />

      <RecordNowModal
        open={recordNowModalOpen}
        results={recordNowResults}
        startAt={recordNowStartAt}
        endAt={recordNowEndAt}
        token={token}
        onClose={() => setRecordNowModalOpen(false)}
        onChanged={() => {
          setRecordNowModalOpen(false);
          refreshSchedules();
        }}
      />

      <ConfirmModal
        open={confirmDeleteRecordId !== null}
        onClose={() => setConfirmDeleteRecordId(null)}
        onConfirm={() => onDelete(confirmDeleteRecordId)}
        title="Confirm Delete"
        message={`Are you sure you want to Delete: "${records.find((record) => record.id === confirmDeleteRecordId)?.title}"?`}
        type={"danger"}
      />

      <ConfirmModal
        open={confirmStopRecordId !== null}
        onClose={() => setConfirmStopRecordId(null)}
        onConfirm={() => {
          onStop(confirmStopRecordId);
        }}
        title="Confirm Stop"
        message={`Are you sure you want to Stop: "${records.find((record) => record.id === confirmStopRecordId)?.title}"?`}
        type={"danger"}
      />

      <ConfirmModal
        open={confirmBulkClearOpen}
        onClose={() => setConfirmBulkClearOpen(false)}
        onConfirm={() => { void clearFutureRecordsOnSelectedSlots(); }}
        title="Confirm Clear"
        message={`Delete all future record items on ${selectedSlotCount} selected slot${selectedSlotCount !== 1 ? 's' : ''}?`}
        type={"danger"}
      />

      <ConfirmModal
        open={confirmBulkStopOpen}
        onClose={() => setConfirmBulkStopOpen(false)}
        onConfirm={() => { void stopActiveRecordsOnSelectedSlots(); }}
        title="Confirm Stop"
        message={`Stop ${activeRecordIdsForSelectedSlots.length} current recording item${activeRecordIdsForSelectedSlots.length !== 1 ? 's' : ''} on the selected slots by setting end time to now + 10 seconds?`}
        type={"danger"}
      />

      <SlotFormModal
        open={slotModalOpen}
        slot={editingSlot}
        token={token}
        mediaengineProfiles={schedulerProfiles}
        sdiResources={sdiResources}
        filesystemResources={filesystemResources}
        rtmpResources={rtmpResources}
        onClose={() => { setSlotModalOpen(false); setEditingSlot(null); }}
        onSave={handleSaveSlot}
        onDelete={handleDeleteSlot}
      />

      <SlotScheduleModal
        open={scheduleModalOpen}
        slotIds={selectedChannelIdsForRecordForm}
        slotLabelById={Object.fromEntries(schedulerSlots.map((s) => [s.id, s.label]))}
        token={token}
        initialStartAt={scheduleModalInitialStart}
        initialEndAt={scheduleModalInitialEnd}
        mediaengineProfiles={schedulerProfiles}
        sdiResources={sdiResources}
        filesystemResources={filesystemResources}
        rtmpResources={rtmpResources}
        onClose={() => setScheduleModalOpen(false)}
        onScheduleCreated={() => {
          setScheduleModalOpen(false);
          refreshSchedules();
        }}
      />
    </>
  );
};

export default Recorder;
